/**
 * V2 Text Field Section Configuration
 *
 * Maps V2 StepNode.content.textField paths for the TextInput screen type.
 *
 * V2 path changes from V1:
 * - field.type → content.textField.fieldType
 * - field.subtype → content.textField.fieldSubtype
 * - field.placeholder.l10n → content.textField.placeholder.translations
 * - field.errorMessage.l10n → content.textField.errorMessage.translations
 * - field.validationRegex → content.textField.validationRegex
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  BOOLEAN_OPTIONS,
  createV2AppearanceStyleFields,
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  NUMBER_0_50_OPTIONS,
  V2_TEXT_ALIGN_OPTIONS,
} from './atomicFields';
import { createV2ImageStyleFields } from './v2OptionsListStyleConfig';

/**
 * Field type options for the dropdown.
 * Since schema 2.7.0, 'email' is a proper FieldType value (no longer synthetic).
 */
export const V2_FIELD_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'int', label: 'Integer' },
  { value: 'double', label: 'Double' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
];

/**
 * Keyboard type options for the dropdown.
 * 'auto' means no explicit keyboardType in schema — runtime derives from fieldType.
 * Other values override the auto-derived keyboard.
 */
export const V2_KEYBOARD_TYPE_OPTIONS = [
  { value: '', label: 'Auto (from field type)' },
  { value: 'default', label: 'Default' },
  { value: 'email', label: 'Email (@)' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
];

/**
 * Placeholder field configuration
 */
export const V2_PLACEHOLDER_CONFIG: FieldConfig = {
  path: 'content.textField.placeholder',
  label: 'Placeholder',
  type: 'text',
  l10nPath: 'content.textField.placeholder',
  defaultValue: '',
  disableBehavior: 'null',
  guideKey: 'screenedit-text-field-placeholder',
  styleFields: [
    {
      path: 'content.textField.placeholder.appearance.color',
      label: 'placeholder color',
      type: 'color',
      defaultValue: '#999999',
      isNullable: true,
      guideKey: 'screenedit-text-field-placeholder-color',
    },
  ],
};

/**
 * Error message field configuration (Email mode only)
 */
export const V2_ERROR_MESSAGE_CONFIG: FieldConfig = {
  path: 'content.textField.errorMessage',
  label: 'Error Message',
  type: 'text',
  l10nPath: 'content.textField.errorMessage',
  defaultValue: '',
  disableBehavior: 'null',
  guideKey: 'screenedit-text-field-error-message',
  styleFields: createV2AppearanceStyleFields('content.textField.errorMessage'),
};

/**
 * Field appearance styles (TextFieldStyle): background, border, text color
 * Rendered as expandable color picker panel next to Field Type selector.
 */
export const V2_FIELD_STYLES_CONFIG: FieldConfig = {
  path: 'content.textField.appearance',
  label: 'Field Styles',
  type: 'select',
  styleFields: [
    // Field container colors
    {
      path: 'content.textField.appearance.enteredTextColor',
      label: 'text color',
      type: 'color',
      defaultValue: '#000000',
      isNullable: false,
      guideKey: 'screenedit-text-field-text-color',
    },
    {
      path: 'content.textField.appearance.backgroundColor',
      label: 'background color',
      type: 'color',
      defaultValue: null,
      isNullable: true,
      guideKey: 'screenedit-text-field-background-color',
    },
    {
      path: 'content.textField.appearance.borderColor',
      label: 'border color',
      type: 'color',
      defaultValue: '#ffc201',
      isNullable: true,
      guideKey: 'screenedit-text-field-border-color',
    },
    {
      path: 'content.textField.appearance.borderWidth',
      label: 'border width',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 1,
      defaultScrollValue: 1,
      isNullable: true,
      guideKey: 'screenedit-text-field-border-width',
    },
    {
      path: 'content.textField.appearance.borderType',
      label: 'border type',
      type: 'select',
      options: [
        { value: 'bottomText', label: 'Bottom (text)' },
        { value: 'bottomTextIcon', label: 'Bottom (text + icon)' },
        { value: 'full', label: 'Full border' },
      ],
      defaultValue: 'bottomText',
      isNullable: true,
      guideKey: 'screenedit-text-field-border-type',
    },
    // Text typography (from placeholder appearance — shared for entered text & placeholder)
    {
      path: 'content.textField.placeholder.appearance.fontFamily',
      label: 'font family',
      type: 'select',
      options: [...FONT_FAMILY_OPTIONS],
      defaultValue: 'SF Pro',
      isNullable: true,
      guideKey: 'screenedit-text-field-font-family',
    },
    {
      path: 'content.textField.placeholder.appearance.fontSize',
      label: 'font size',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 15,
      defaultScrollValue: 18,
      isNullable: true,
      guideKey: 'screenedit-text-field-font-size',
    },
    {
      path: 'content.textField.placeholder.appearance.fontWeight',
      label: 'font weight',
      type: 'select',
      options: [...FONT_WEIGHT_OPTIONS_SHORT],
      defaultValue: 400,
      isNullable: true,
      numericValue: true,
      guideKey: 'screenedit-text-field-font-weight',
    },
    {
      path: 'content.textField.placeholder.appearance.lineHeight',
      label: 'line height',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 18,
      defaultScrollValue: 18,
      isNullable: true,
      guideKey: 'screenedit-text-field-line-height',
    },
    {
      path: 'content.textField.placeholder.appearance.align',
      label: 'text align',
      type: 'select',
      options: [...V2_TEXT_ALIGN_OPTIONS],
      defaultValue: 'center',
      isNullable: true,
      guideKey: 'screenedit-text-field-text-align',
    },
  ],
};

/**
 * Optional icon displayed to the left of the text input.
 * Uses LocalizedImage — size and corner radius from image appearance.dimensions / cornerRadii.
 * Padding between icon and input via layout.insets.end.
 */
export const V2_ICON_CONFIG: FieldConfig = {
  path: 'content.textField.icon',
  label: 'Icon',
  type: 'image',
  l10nPath: 'content.textField.icon',
  defaultValue: null,
  disableBehavior: 'null',
  guideKey: 'screenedit-text-field-icon',
  styleFields: [
    ...createV2ImageStyleFields('content.textField.icon'),
    {
      path: 'content.textField.icon.layout.insets.end',
      label: 'gap (right)',
      type: 'numberCombo',
      defaultValue: 8,
      isNullable: true,
      guideKey: 'screenedit-text-field-icon-gap',
    },
  ],
};

/**
 * Validation regex field configuration
 */
export const V2_VALIDATION_REGEX_CONFIG: FieldConfig = {
  path: 'content.textField.validationRegex',
  label: 'Validation Regex',
  type: 'text',
  defaultValue: '',
  placeholder: 'e.g. ^[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}$',
  guideKey: 'screenedit-text-field-validation-regex',
};

/**
 * Auto-focus toggle: whether to focus the text input when the screen appears.
 * Schema default: true (auto-focus enabled).
 */
export const V2_AUTO_FOCUS_CONFIG: FieldConfig = {
  path: 'content.textField.setFocusWhenScreenAppeared',
  label: 'Auto-focus on appear',
  type: 'select',
  options: [...BOOLEAN_OPTIONS],
  defaultValue: 'true',
  isNullable: true,
  guideKey: 'screenedit-text-field-auto-focus',
};
